import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys, LIVE_REFETCH_MS } from "@/api/queryClient";
import type { Node, Agent } from "@/types";

export function useNodes(opts?: { enabled?: boolean }): UseQueryResult<Node[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: queryKeys.list("nodes"),
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<Node[]>("/nodes");
    },
    // #299 (Review M-3): der Aufrufer darf den Abruf unterdruecken, wenn die Sicht
    // gar nicht freigegeben ist — sonst feuert die Sidebar auf JEDER Seite garantierte
    // 403er (drei pro Seitenaufruf, mit retry + refetchOnWindowFocus).
    enabled: !!session && (opts?.enabled ?? true),
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useNode(id: string | undefined): UseQueryResult<Node> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("nodes", id) : ["resources", "nodes", "_invalid"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<Node>(`/nodes/${id}`);
    },
    enabled: !!id && !!session,
  });
}

// #177: Liste aller Agents die diesem Node assigned sind. Cross-Sicht für
// Admin-Decommission.
export function useNodeAgents(id: string | undefined): UseQueryResult<Agent[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? [...queryKeys.detail("nodes", id), "agents"] : ["resources", "nodes", "_invalid", "agents"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<Agent[]>(`/nodes/${id}/agents`);
    },
    enabled: !!id && !!session,
  });
}
